import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import api from "../../../../api/api";
import QuestionView from "../../Question/View/QuestionView";

const DeepSeekConvInfoModal = ({ show, onHide, activeConv, selectedUser, t }) => {
    const [activeTab, setActiveTab] = useState("lesson"); // "lesson", "questions", "topics"
    const [selectedBotTurnIndex, setSelectedBotTurnIndex] = useState(0);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedQuestionHistoryIndex, setSelectedQuestionHistoryIndex] = useState(-1);
    const [zoomLevel, setZoomLevel] = useState(1.0);
    const [resolvedQuestions, setResolvedQuestions] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(false);

    const botTurns = activeConv && activeConv.turns ? activeConv.turns.filter(turn => turn.IsFromBot) : [];
    const activeTurn = botTurns[selectedBotTurnIndex];
    const hasAnyQuestions = botTurns.some(turn =>
        (turn.Questions && turn.Questions.length > 0) ||
        (turn.QuestionsHistory && turn.QuestionsHistory.some(hist => (hist.questions && hist.questions.length > 0) || (hist.Questions && hist.Questions.length > 0)))
    );

    let recommendationObj = null;
    if (activeConv && activeConv.recommendationTopic) {
        try {
            recommendationObj = JSON.parse(activeConv.recommendationTopic);
        } catch (e) {
            recommendationObj = { topic: activeConv.recommendationTopic };
        }
    }

    // Reset when modal is shown or conversation changes
    useEffect(() => {
        if (show) {
            setActiveTab("lesson");
            setSelectedBotTurnIndex(botTurns.length > 0 ? botTurns.length - 1 : 0);
            setCurrentQuestionIndex(0);
            setSelectedQuestionHistoryIndex(-1);
            setZoomLevel(1.0);
            setResolvedQuestions([]);
        }
    }, [show, activeConv, botTurns.length]);

    // Reset question selection when turn changes
    useEffect(() => {
        setCurrentQuestionIndex(0);
        setSelectedQuestionHistoryIndex(-1);
        setResolvedQuestions([]);
    }, [selectedBotTurnIndex]);

    // Resolve questions using QuestionsLookupsController
    useEffect(() => {
        if (!show || !activeTurn) {
            setResolvedQuestions([]);
            return;
        }

        const rawQuestions = selectedQuestionHistoryIndex === -1
            ? (activeTurn.Questions || [])
            : ((activeTurn.QuestionsHistory && activeTurn.QuestionsHistory[selectedQuestionHistoryIndex]?.questions)
                || (activeTurn.QuestionsHistory && activeTurn.QuestionsHistory[selectedQuestionHistoryIndex]?.Questions)
                || []);

        if (rawQuestions.length === 0) {
            setResolvedQuestions([]);
            return;
        }

        setLoadingQuestions(true);
        const payload = rawQuestions.map(q => ({
            isFromDB: q.IsFromDB || q.isFromDB || false,
            questionId: q.QuestionId || q.questionId || (q.isFromDB ? q.id || q.Id : null),
            lessonId: q.LessonId || q.lessonId || "",
            id: q.id || q.Id || "",
            type: q.Type || q.type || "",
            questionText: q.QuestionText || q.questionText || q.Name || q.name || "",
            options: q.Options || q.options || null,
            correctAnswers: q.CorrectAnswers || q.correctAnswers || [],
            categories: q.Categories || q.categories || null,
            answers: q.Answers || q.answers || null,
            correctMapping: q.CorrectMapping || q.correctMapping || null,
            customStyle: q.CustomStyle || q.customStyle || null,
            hint: q.hint || q.Hint || "",
            wordAndAdvice: q.wordAndAdvice || ""
        }));

        api.post('/QuestionsLookups', payload)
            .then(res => {
                setResolvedQuestions(res.data.data || []);
                setLoadingQuestions(false);
            })
            .catch(() => {
                setResolvedQuestions(rawQuestions);
                setLoadingQuestions(false);
            });
    }, [show, activeTurn, selectedQuestionHistoryIndex]);

    if (!activeConv) return null;

    const cleanBotHtml = (html) => {
        if (!html) return "";
        let cleaned = html;
        cleaned = cleaned.replace(/\*\*([\s\S]*?)\*\*/g, "<b>$1</b>");
        return cleaned;
    };

    // Calculate billing values
    const totalPromptTokens = activeConv.promptTokens || 0;
    const totalCompletionTokens = activeConv.completionTokens || 0;
    const totalTokens = totalPromptTokens + totalCompletionTokens;
    const totalBillPrice = activeConv.price || 0;

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="xl"
            centered
            dialogClassName="deepseek-conv-modal"
            style={{ direction: "rtl" }}
        >
            <Modal.Header closeButton className="text-right">
                <div className="d-flex align-items-center justify-content-between w-100 pl-4" style={{ direction: 'rtl' }}>
                    <Modal.Title className="h5 font-weight-bold m-0 d-flex align-items-center gap-2">
                        <i className="fal fa-comments-alt text-primary ml-2" style={{ color: '#92278f' }}></i>
                        <span>{t('Pages.DeepSeekAdmin.fullConversationDetails')}</span>
                        <span className="text-primary mr-1">{activeConv.title}</span>
                    </Modal.Title>
                    <button
                        type="button"
                        className="btn btn-xs btn-outline-secondary ml-3 mr-auto d-flex align-items-center gap-1"
                        onClick={(e) => {
                            e.stopPropagation();
                            const url = `${window.location.origin}/ControlPanel/DeepSeek/${activeConv.id}`;
                            navigator.clipboard.writeText(url).then(() => {
                                toast.success(t('Pages.DeepSeekAdmin.linkCopied') || "تم نسخ رابط المحادثة بنجاح!");
                            });
                        }}
                        style={{ padding: '4px 10px', borderRadius: '8px', fontSize: '11px', width: 'auto' }}
                    >
                        <i className="fal fa-link"></i>
                        <span>{t('Pages.DeepSeekAdmin.copyLink') || "نسخ الرابط المباشر"}</span>
                    </button>
                </div>
            </Modal.Header>
            <Modal.Body className="text-right">
                <div className="more-info-layout-wrapper">
                    {/* Right Panel: Main details (Usage info or questions) */}
                    <div className="more-info-main-panel">
                        {/* Selector for Bot Turn Stage */}
                        {botTurns.length > 1 && (
                            <div className="mb-3 d-flex align-items-center gap-2 pb-3 border-bottom">
                                <span className="font-weight-bold text-muted small ml-2">{t('Pages.DeepSeekAdmin.conversationStage')}</span>
                                <select
                                    className="form-control form-control-sm"
                                    value={selectedBotTurnIndex}
                                    onChange={(e) => setSelectedBotTurnIndex(parseInt(e.target.value))}
                                    style={{ width: 'auto', minWidth: '220px', borderRadius: '8px', fontFamily: 'Cairo, sans-serif' }}
                                >
                                    {botTurns.map((turn, bIdx) => {
                                        const titleText = turn.Answer || turn.Message || "";
                                        const shortTitle = titleText.replace(/<[^>]*>?/gm, "").substring(0, 35) + "...";
                                        return (
                                            <option key={bIdx} value={bIdx}>
                                                {t('Pages.DeepSeekAdmin.responsePrefix')}{bIdx + 1} ({new Date(turn.DateTime).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })}) - {shortTitle}
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                        )}

                        {activeTab === "lesson" && activeTurn && (
                            <div className="animate__animated animate__fadeIn animate__faster">
                                <div className="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3 flex-wrap gap-2">
                                    <h5 className="font-weight-bold m-0 text-dark d-flex align-items-center gap-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
                                        <i className="fal fa-book-open text-primary" style={{ color: '#92278f' }}></i>
                                        <span>{t('Pages.DeepSeekAdmin.generatedLesson')}</span>
                                    </h5>
                                    <div className="lesson-zoom-toolbar">
                                        <button type="button" className="zoom-btn btn btn-sm btn-link text-muted p-1" onClick={() => setZoomLevel(prev => Math.min(prev + 0.1, 1.8))}>
                                            <i className="fal fa-plus"></i>
                                        </button>
                                        <button type="button" className="zoom-btn btn btn-sm btn-link text-muted p-1" onClick={() => setZoomLevel(1.0)}>
                                            <i className="fal fa-history"></i>
                                        </button>
                                        <button type="button" className="zoom-btn btn btn-sm btn-link text-muted p-1" onClick={() => setZoomLevel(prev => Math.max(prev - 0.1, 0.7))}>
                                            <i className="fal fa-minus"></i>
                                        </button>
                                    </div>
                                </div>

                                <div className="visual-theater-card card p-4 mb-3 border shadow-sm rounded-lg bg-white overflow-auto text-right" style={{ maxHeight: '450px' }}>
                                    <div
                                        className="bot-answer-html text-right"
                                        style={{ fontSize: `${14 * zoomLevel}px`, lineHeight: '1.7', unicodeBidi: 'plaintext' }}
                                        dangerouslySetInnerHTML={{ __html: cleanBotHtml(activeTurn.Answer || activeTurn.Message) }}
                                    />
                                </div>
                            </div>
                        )}

                        {activeTab === "questions" && activeTurn && (
                            <div className="d-flex flex-column h-100 animate__animated animate__fadeIn animate__faster">
                                {loadingQuestions ? (
                                    <div className="text-center py-5 text-muted">
                                        <i className="fal fa-spinner fa-spin mb-2 h4 d-block"></i>
                                        <span>{t('Pages.DeepSeekAdmin.loadingQuestions')}</span>
                                    </div>
                                ) : (
                                    (() => {
                                        const q = resolvedQuestions[currentQuestionIndex];

                                        return (
                                            <>
                                                {/* Questions tracker header */}
                                                <div className="d-flex align-items-center justify-content-between pb-3 border-bottom mb-3 flex-wrap gap-2">
                                                    <h5 className="font-weight-bold m-0 text-dark d-flex align-items-center gap-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
                                                        <i className="fal fa-graduation-cap text-primary" style={{ color: '#92278f' }}></i>
                                                        <span>{t('Pages.DeepSeekAdmin.exercisesTitle')} ({resolvedQuestions.length > 0 ? `${currentQuestionIndex + 1} ${t('Pages.Chatbot.From')} ${resolvedQuestions.length}` : t('Pages.DeepSeekAdmin.noQuestions')})</span>
                                                    </h5>

                                                    {/* Questions history selector */}
                                                    {activeTurn.QuestionsHistory && activeTurn.QuestionsHistory.length > 0 && (
                                                        <div className="d-flex align-items-center">
                                                            <label className="text-muted small m-0 ml-2 font-weight-bold" style={{ whiteSpace: "nowrap" }}>
                                                                {t('Pages.DeepSeekAdmin.questionVersion')}
                                                            </label>
                                                            <select
                                                                className="form-control form-control-sm rounded-pill px-2"
                                                                style={{ width: "auto", minWidth: "150px", height: '28px', fontSize: '11px', fontFamily: 'Cairo, sans-serif' }}
                                                                value={selectedQuestionHistoryIndex}
                                                                onChange={(e) => {
                                                                    setSelectedQuestionHistoryIndex(parseInt(e.target.value));
                                                                    setCurrentQuestionIndex(0);
                                                                }}
                                                            >
                                                                <option value={-1}>{t('Pages.DeepSeekAdmin.currentVersion')} ({activeTurn.Questions?.length || 0} {t('Pages.DeepSeekAdmin.questionCount')})</option>
                                                                {activeTurn.QuestionsHistory.map((hist, hIdx) => (
                                                                    <option key={hIdx} value={hIdx}>
                                                                        {t('Pages.DeepSeekAdmin.versionNum', { num: hIdx + 1 })} ({hist.questions?.length || hist.Questions?.length || 0} {t('Pages.DeepSeekAdmin.questionCount')})
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    )}
                                                </div>

                                                {resolvedQuestions.length > 0 ? (
                                                    <>
                                                        {/* Tracker dots */}
                                                        <div className="questions-progress-tracker d-flex justify-content-center flex-wrap gap-2 mb-3">
                                                            {resolvedQuestions.map((_, idx) => {
                                                                const isActive = idx === currentQuestionIndex;
                                                                return (
                                                                    <div
                                                                        key={idx}
                                                                        className={`progress-dot ${isActive ? 'active' : ''}`}
                                                                        title={`${t('Pages.Chatbot.QuestionNumber') || "سؤال"} ${idx + 1}`}
                                                                        onClick={() => setCurrentQuestionIndex(idx)}
                                                                        style={{
                                                                            width: '28px',
                                                                            height: '28px',
                                                                            borderRadius: '50%',
                                                                            background: isActive ? '#92278f' : '#e2e8f0',
                                                                            color: isActive ? '#ffffff' : '#475569',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontSize: '11px',
                                                                            fontWeight: 'bold',
                                                                            cursor: 'pointer',
                                                                            userSelect: 'none',
                                                                            border: '1px solid rgba(14, 44, 83, 0.05)',
                                                                            boxShadow: isActive ? '0 0 0 3px rgba(146, 39, 143, 0.2)' : 'none',
                                                                            transform: isActive ? 'scale(1.1)' : 'none',
                                                                            transition: 'all 0.2s ease'
                                                                        }}
                                                                    >
                                                                        {idx + 1}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>

                                                        {/* Question card */}
                                                        <div className="visual-theater-card card p-3 p-md-4 mb-3 border shadow-sm rounded-lg bg-white text-right">
                                                            {q && (
                                                                <div className="d-flex flex-column h-100 text-right">
                                                                    <div className="question-renderer-wrapper flex-grow-1 text-right mb-3">
                                                                        <QuestionView
                                                                            key={q.Id || q.id || `q_${currentQuestionIndex}`}
                                                                            question={q}
                                                                            answers={q.Answers || q.answers}
                                                                            index={currentQuestionIndex + 1}
                                                                            indexType={1}
                                                                            examAnswers={[]}
                                                                            saveExamAnswer={() => { }}
                                                                            onEdit={false}
                                                                            solveButton={false}
                                                                            FontSize={18}
                                                                            questionSeparated={false}
                                                                            isExamQuestion={false}
                                                                        />
                                                                    </div>

                                                                    {/* Hint */}
                                                                    {(q.hint || q.Hint) && (
                                                                        <div className="alert alert-info border-info text-right p-3 rounded shadow-sm mt-3" style={{ borderRight: '4px solid #17a2b8' }}>
                                                                            <h6 className="font-weight-bold text-info mb-1 text-right" style={{ fontSize: '12.5px' }}>
                                                                                <i className="fal fa-lightbulb-on ml-2"></i>{t('Pages.Chatbot.HintTitle')}
                                                                            </h6>
                                                                            <p className="m-0 text-dark small text-right" style={{ lineHeight: '1.5' }}>
                                                                                {q.hint || q.Hint}
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Navigation row */}
                                                        <div className="question-navigation-row d-flex align-items-center justify-content-between border-top pt-3 mt-auto">
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary btn-sm rounded-pill px-3 d-inline-flex align-items-center gap-1"
                                                                disabled={currentQuestionIndex === 0}
                                                                onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
                                                                style={{ borderRadius: '20px' }}
                                                            >
                                                                <i className="fal fa-arrow-right ml-1"></i>
                                                                <span>{t('Pages.DeepSeekAdmin.prev')}</span>
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="btn btn-primary btn-sm rounded-pill px-3 d-inline-flex align-items-center gap-1"
                                                                disabled={currentQuestionIndex === resolvedQuestions.length - 1}
                                                                onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                                                                style={{ borderRadius: '20px', backgroundColor: '#92278f', borderColor: '#92278f' }}
                                                            >
                                                                <span>{t('Pages.DeepSeekAdmin.next')}</span>
                                                                <i className="fal fa-arrow-left mr-1"></i>
                                                            </button>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="text-center py-5 text-muted">
                                                        <i className="fal fa-exclamation-triangle mb-2 h4 d-block"></i>
                                                        <span>{t('Pages.DeepSeekAdmin.noQuestionsInResponse')}</span>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()
                                )}
                            </div>
                        )}

                        {activeTab === "topics" && activeTurn && (
                            <div className="animate__animated animate__fadeIn animate__faster">
                                <h5 className="font-weight-bold pb-3 border-bottom mb-3 text-dark d-flex align-items-center gap-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
                                    <i className="fal fa-tags text-primary" style={{ color: '#92278f' }}></i>
                                    <span>{t('Pages.DeepSeekAdmin.suggestedTopicsInResponse')}</span>
                                </h5>

                                {activeTurn.SearchWords && activeTurn.SearchWords.length > 0 ? (
                                    <div className="d-flex flex-wrap gap-2 mt-3">
                                        {activeTurn.SearchWords.map((word, wIdx) => (
                                            <span key={wIdx} className="deepseek-modal-badge" style={{ fontSize: '13px', padding: '8px 16px' }}>
                                                <i className="fal fa-hashtag text-primary" style={{ color: '#92278f' }}></i> {word}
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-5 text-muted">
                                        <i className="fal fa-info-circle mb-2 h4 d-block"></i>
                                        <span>{t('Pages.DeepSeekAdmin.noSuggestedTopicsInResponse')}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === "relativeLessons" && activeTurn && (
                            <div className="animate__animated animate__fadeIn animate__faster">
                                <h5 className="font-weight-bold pb-3 border-bottom mb-3 text-dark d-flex align-items-center gap-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
                                    <i className="fal fa-link text-primary" style={{ color: '#92278f' }}></i>
                                    <span>{t('Pages.Chatbot.RelativeLessons') || "الدروس المقترحة ذات الصلة"}</span>
                                </h5>

                                {activeTurn.RelativeLessons && activeTurn.RelativeLessons.length > 0 ? (
                                    <div className="d-flex flex-column gap-2 mt-3">
                                        {activeTurn.RelativeLessons.map((lesson, lIdx) => {
                                            const siteName = window.location.origin;
                                            let url = "#";
                                            if (lesson.Type === "File") {
                                                url = `${siteName}${lesson.FileUrl || ""}`;
                                            } else {
                                                const urlPrefix = lesson.Type === "Game" ? "GameView" : lesson.Type === "Exam" ? "ExamView" : "LessonView";
                                                url = `${siteName}/${urlPrefix}/${lesson.Id}`;
                                            }
                                            return (
                                                <a
                                                    key={lIdx}
                                                    href={url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="d-flex align-items-center justify-content-between p-3 rounded border text-decoration-none bg-light text-dark"
                                                    style={{ transition: 'all 0.2s ease', borderRight: '4px solid #92278f' }}
                                                >
                                                    <div className="d-flex align-items-center gap-3">
                                                        <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px', backgroundColor: 'rgba(146, 39, 143, 0.1)', color: '#92278f' }}>
                                                            <i className={lesson.Type === "File" ? "fal fa-file-alt" : lesson.Type === "Game" ? "fal fa-gamepad" : lesson.Type === "Exam" ? "fal fa-file-signature" : "fal fa-book"}></i>
                                                        </div>
                                                        <div className="mr-3">
                                                            <h6 className="m-0 font-weight-bold text-right" style={{ fontSize: '14px' }}>{lesson.Name}</h6>
                                                            <span className="text-muted small text-right d-block mt-1">{lesson.Type === "Game" ? t('Pages.Chatbot.IllustratedCrimes') || "لعبة" : lesson.Type === "Exam" ? t('Pages.Chatbot.InteractiveExercises') || "تمرين" : t('Pages.Chatbot.GeneratedLesson') || "درس"}</span>
                                                        </div>
                                                    </div>
                                                    <i className="fal fa-external-link text-muted"></i>
                                                </a>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-5 text-muted">
                                        <i className="fal fa-exclamation-triangle mb-2 h4 d-block"></i>
                                        <span>{t('Pages.Chatbot.NoRelativeLessons') || "لا توجد دروس مقترحة"}</span>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === "recommendation" && recommendationObj && (() => {
                            const recData = recommendationObj.recommendation || {};
                            const recTopicName = recData.topicName || recommendationObj.topic || "";
                            const recDesc = recData.description || recommendationObj.description || "";
                            const choicesList = recData.choices || [];
                            const recTimeSec = recommendationObj.time ? (recommendationObj.time / 1000).toFixed(2) : null;

                            return (
                                <div className="animate__animated animate__fadeIn animate__faster text-right">
                                    <h5 className="font-weight-bold pb-3 border-bottom mb-3 text-dark d-flex align-items-center gap-2" style={{ fontFamily: 'Cairo, sans-serif' }}>
                                        <i className="fal fa-lightbulb text-warning" style={{ color: '#f59e0b' }}></i>
                                        <span>{t('Pages.DeepSeekAdmin.nextTopicRecommendation') || "التوصية بالموضوع التالي"}</span>
                                    </h5>

                                    {recData.introText && (
                                        <div className="alert alert-warning border-warning p-3 rounded mb-3 text-right" style={{ borderRight: '4px solid #f59e0b', fontSize: '13.5px', lineHeight: '1.6', color: '#854d0e', backgroundColor: '#fef9c3' }}>
                                            {recData.introText}
                                        </div>
                                    )}

                                    {/* Selected Main recommendation */}
                                    {recTopicName && (
                                        <div className="card border-0 shadow-sm rounded-lg overflow-hidden bg-white mb-4 text-right" style={{ borderRight: '5px solid #92278f' }}>
                                            <div className="p-3" style={{ background: 'linear-gradient(135deg, #fdf4fd 0%, #f5f3ff 100%)' }}>
                                                <span className="text-muted small d-block mb-1">{t('Pages.DeepSeekAdmin.recommendedTopicLabel') || "الموضوع المقترح المختار"}</span>
                                                <h4 className="font-weight-bold m-0" style={{ fontSize: '17px', color: '#92278f' }}>{recTopicName}</h4>
                                            </div>
                                            {recDesc && (
                                                <div className="p-3 bg-white text-muted small border-top" style={{ lineHeight: '1.6' }}>
                                                    {recDesc}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Alternate Choices list */}
                                    {choicesList.length > 0 && (
                                        <div className="mb-4 text-right">
                                            <h6 className="font-weight-bold text-dark mb-2" style={{ fontSize: '13px' }}>{t('Pages.DeepSeekAdmin.availableChoices') || "الخيارات والبدائل المقترحة الأخرى:"}</h6>
                                            <div className="d-flex flex-column gap-2" style={{ gap: '8px' }}>
                                                {choicesList.map((ch, chIdx) => {
                                                    const isSelected = ch.topicName === recTopicName;
                                                    return (
                                                        <div key={chIdx} className="card border p-3 rounded bg-white text-right shadow-sm" style={{ borderRight: isSelected ? '4px solid #92278f' : '1px solid #e2e8f0', borderRadius: '8px' }}>
                                                            <div className="d-flex align-items-center justify-content-between">
                                                                <span className="font-weight-bold text-dark" style={{ fontSize: '13.5px' }}>{ch.topicName}</span>
                                                                {isSelected && <span className="badge badge-primary font-weight-bold px-2 py-1" style={{ backgroundColor: '#92278f', fontSize: '10px', borderRadius: '12px' }}>{t('Pages.DeepSeekAdmin.selectedChoice') || "المختار"}</span>}
                                                            </div>
                                                            {ch.description && <p className="text-muted small m-0 mt-2" style={{ lineHeight: '1.5' }}>{ch.description}</p>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Pricing & Performance Metadata */}
                                    <div className="card border shadow-sm rounded-lg overflow-hidden bg-white mt-3 text-right" style={{ borderRadius: '8px' }}>
                                        <div className="p-3 bg-light font-weight-bold text-dark border-bottom small">
                                            <i className="fal fa-chart-line ml-2"></i> {t('Pages.DeepSeekAdmin.performanceDetails') || "تفاصيل أداء التوصية"}
                                        </div>
                                        <div className="p-3">
                                            <div className="row">
                                                {recTimeSec && (
                                                    <div className="col-md-4 mb-2">
                                                        <span className="text-muted small d-block mb-1">{t('Pages.DeepSeekAdmin.recommendationTime') || "وقت التوصية (بالثواني)"}</span>
                                                        <strong className="text-dark" style={{ fontSize: '13.5px' }}>{recTimeSec} {t('Pages.Chatbot.SecondsAbbr') || "ث"}</strong>
                                                    </div>
                                                )}
                                                {recommendationObj.agent && (
                                                    <div className="col-md-4 mb-2">
                                                        <span className="text-muted small d-block mb-1">{t('Pages.DeepSeekAdmin.recommendationModel') || "النموذج المستخدم للتوصية"}</span>
                                                        <strong className="text-dark" style={{ fontSize: '13.5px' }}>{recommendationObj.agent}</strong>
                                                    </div>
                                                )}
                                                {recommendationObj.price !== undefined && (
                                                    <div className="col-md-4 mb-2">
                                                        <span className="text-muted small d-block mb-1">{t('Pages.DeepSeekAdmin.recommendationCost') || "تكلفة التوصية بالدولار"}</span>
                                                        <strong className="text-success" style={{ fontSize: '14px' }}>${parseFloat(recommendationObj.price).toFixed(6)}</strong>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {!activeTurn && (
                            <div className="text-center py-5 text-muted">
                                <i className="fal fa-spinner fa-spin mb-2 h4 d-block"></i>
                                <span>{t('Pages.DeepSeekAdmin.loadingResponseDetails')}</span>
                            </div>
                        )}
                    </div>

                    {/* Left Panel: Control and Navigation actions sidebar */}
                    <div className="more-info-sidebar-panel">
                        <div className="sidebar-section-header-modern">
                            <i className="fal fa-sliders-h ml-2 text-primary" style={{ color: '#92278f' }}></i>
                            <span>{t('Pages.DeepSeekAdmin.sidebarTabsHeader')}</span>
                        </div>

                        <div className="sidebar-actions-scroll">
                            <button
                                type="button"
                                className={`sidebar-action-btn-modern ${activeTab === "lesson" ? "active" : ""}`}
                                onClick={() => setActiveTab("lesson")}
                            >
                                <i className="fal fa-book-open icon ml-2"></i>
                                <span>{t('Pages.Chatbot.GeneratedLesson')}</span>
                            </button>

                            {hasAnyQuestions && (
                                <button
                                    type="button"
                                    className={`sidebar-action-btn-modern ${activeTab === "questions" ? "active" : ""}`}
                                    onClick={() => setActiveTab("questions")}
                                >
                                    <i className="fal fa-graduation-cap icon ml-2"></i>
                                    <span>{t('Pages.Chatbot.InteractiveExercises')}</span>
                                </button>
                            )}

                            <button
                                type="button"
                                className={`sidebar-action-btn-modern ${activeTab === "topics" ? "active" : ""}`}
                                onClick={() => setActiveTab("topics")}
                            >
                                <i className="fal fa-tags icon ml-2"></i>
                                <span>{t('Pages.DeepSeekAdmin.suggestedTopicsTab')}</span>
                            </button>

                            {activeTurn && activeTurn.RelativeLessons && activeTurn.RelativeLessons.length > 0 && (
                                <button
                                    type="button"
                                    className={`sidebar-action-btn-modern ${activeTab === "relativeLessons" ? "active" : ""}`}
                                    onClick={() => setActiveTab("relativeLessons")}
                                >
                                    <i className="fal fa-link icon ml-2"></i>
                                    <span>{t('Pages.Chatbot.RelativeLessonsTab') || "دروس ذات صلة"}</span>
                                </button>
                            )}

                            {recommendationObj && (
                                <button
                                    type="button"
                                    className={`sidebar-action-btn-modern ${activeTab === "recommendation" ? "active" : ""}`}
                                    onClick={() => setActiveTab("recommendation")}
                                >
                                    <i className="fal fa-lightbulb icon ml-2"></i>
                                    <span>{t('Pages.DeepSeekAdmin.recommendationTab') || "التوصية التالية"}</span>
                                </button>
                            )}

                            {/* Summary Cost Card inside Sidebar */}
                            <div className="mt-4 p-3 rounded" style={{ background: 'linear-gradient(135deg, #fdf4fd 0%, #f5f3ff 100%)', border: '1px solid #f1e7f1' }}>
                                <div className="font-weight-bold text-dark mb-2" style={{ fontSize: '12px', borderBottom: '1px solid rgba(146, 39, 143, 0.1)', paddingBottom: '4px' }}>
                                    {t('Pages.DeepSeekAdmin.conversationUsageSummary')}
                                </div>
                                <div className="d-flex justify-content-between mb-1 small text-muted">
                                    <span>{t('Pages.DeepSeekAdmin.startMethod') || "طريقة البدء"}</span>
                                    <strong className={activeConv.startFromTopic ? "text-success" : "text-primary"}>
                                        {activeConv.startFromTopic ? (t('Pages.DeepSeekAdmin.startFromTopicLabel') || "اقتراح موضوع") : (t('Pages.DeepSeekAdmin.startDirectLabel') || "سؤال مباشر")}
                                    </strong>
                                </div>
                                <div className="d-flex justify-content-between mb-1 small text-muted">
                                    <span>{t('Pages.DeepSeekAdmin.sentTokensLabel')}</span>
                                    <strong className="text-dark">{totalPromptTokens.toLocaleString()}</strong>
                                </div>
                                <div className="d-flex justify-content-between mb-1 small text-muted">
                                    <span>{t('Pages.DeepSeekAdmin.receivedTokensLabel')}</span>
                                    <strong className="text-dark">{totalCompletionTokens.toLocaleString()}</strong>
                                </div>
                                <div className="d-flex justify-content-between mb-2 small text-muted border-bottom pb-1">
                                    <span>{t('Pages.DeepSeekAdmin.totalTokens')}:</span>
                                    <strong className="text-dark">{totalTokens.toLocaleString()}</strong>
                                </div>
                                <div className="d-flex justify-content-between">
                                    <span className="font-weight-bold text-dark" style={{ fontSize: '12px' }}>{t('Pages.DeepSeekAdmin.conversationCostLabel')}</span>
                                    <strong className="text-success" style={{ fontSize: '13px' }}>${totalBillPrice.toFixed(4)}</strong>
                                </div>
                            </div>

                            <button
                                type="button"
                                className="sidebar-action-btn-modern danger-zone-btn mt-4"
                                onClick={onHide}
                            >
                                <i className="fal fa-times-circle icon ml-2 text-danger" style={{ color: '#ef4444' }}></i>
                                <span>{t('Pages.DeepSeekAdmin.closeWindow')}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default DeepSeekConvInfoModal;
